import { faker } from '@faker-js/faker'
// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { InfoIcon, SaveIcon, UserPlusIcon } from 'lucide-react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { type Employee } from '../types'
import { getDateString, getUTCToday } from '../utils'
import AppConfigContext from './context/AppConfigContext'
import {
  FormDateInputField,
  FormSelectField,
  FormTelephoneInputDS,
  FormTextField,
} from './reusable/form/FormFields'
import { ThatchTooltip } from './reusable/Tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet'

type SubmissionError = {
  message: string
  errors?: string[]
}

type EmployeeInput = {
  first_name: string
  last_name: string
  personal_email: string
  work_email: string
  phone_number: string
  employment_subtype: string
  pay_type: string
  start_date: string
  native_employee_id: string
}

type ChildProps = {
  children?: React.ReactNode
}

export const CreateOrEditEmployeeContext = createContext({
  isOpen: false,
  employee: undefined as Employee | undefined,
  openCreateOrEditEmployee: (_?: Employee | undefined) => {},
  closeCreateOrEditEmployee: () => {},
})

function useCreateOrEditEmployeeProvider () {
  const [isOpen, setIsOpen] = useState(false)
  const [employee, setEmployee] = useState<Employee | undefined>(undefined)

  function openCreateOrEditEmployee (employee?: Employee) {
    setEmployee(employee)
    setIsOpen(true)
  }

  function closeCreateOrEditEmployee () {
    setEmployee(undefined)
    setIsOpen(false)
  }

  return {
    isOpen,
    employee,
    openCreateOrEditEmployee,
    closeCreateOrEditEmployee,
  }
}

export function CreateOrEditEmployeeProvider ({ children }: ChildProps) {
  const value = useCreateOrEditEmployeeProvider()

  return (
    <CreateOrEditEmployeeContext.Provider value={value}>
      {children}
    </CreateOrEditEmployeeContext.Provider>
  )
}

export function useCreateOrEditEmployee () {
  return useContext(CreateOrEditEmployeeContext)
}

function validatePayType (isCreate: boolean, value: string): string | undefined {
  if (isCreate) {
    return !value ? 'Pay type is required' : undefined
  }
  return undefined
}

export function CreateOrEditEmployee ({
  fetchEmployees,
  handleAddAndInvite,
}: {
  fetchEmployees: () => void
  handleAddAndInvite: (employee: Employee) => void
}) {
  const {
    isOpen,
    openCreateOrEditEmployee,
    closeCreateOrEditEmployee,
    employee,
  } = useCreateOrEditEmployee()
  const [submissionError, setSubmissionError] = useState<
    SubmissionError | undefined
  >()
  const [submitting, setSubmitting] = useState<boolean>(false)
  const isCreate = !employee

  const { environment } = useContext(AppConfigContext)

  // Fill in the form with demo values if we're not in production.
  const prepopulatedDemoValues =
    environment === 'production' || employee
      ? {}
      : {
          first_name: faker.person.firstName(),
          last_name: faker.person.lastName(),
          // Generate a random email to avoid conflicts with existing employees. Number generated is between 0-999
          // and padded with 0s.
          work_email: `${faker.internet.username()}_${Math.floor(
            Math.random() * 100000
          )
            .toString()
            .padStart(3, '0')}@thatch.test`,
        }

  // This hook ensures the employee form is prepopulated with the employee's data when editing an employee. This is needed
  // since the form initially mounts with empty values.
  useEffect(() => {
    if (!employee) {
      return
    }

    setValue('first_name', employee?.first_name || '')
    setValue('last_name', employee?.last_name || '')
    setValue('personal_email', employee?.personal_email || '')
    setValue('work_email', employee?.work_email || '')
    setValue('employment_subtype', employee?.employment_subtype || '')
    setValue('pay_type', employee?.pay_type || '')
    setValue('phone_number', employee?.phone_number || '')
    setValue(
      'start_date',
      employee?.start_date ? getDateString(new Date(employee?.start_date)) : ''
    )
    setValue('native_employee_id', employee?.native_employee_id || '')
  }, [employee])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<EmployeeInput>({
    defaultValues: {
      first_name: employee?.first_name || '',
      last_name: employee?.last_name || '',
      personal_email: employee?.personal_email || '',
      work_email: employee?.work_email || '',
      employment_subtype: employee?.employment_subtype || 'full_time',
      pay_type: isCreate ? 'salary' : employee?.pay_type,
      start_date: isCreate ? getDateString(getUTCToday()) : employee?.start_date,
      phone_number: employee?.phone_number || '',
      native_employee_id: employee?.native_employee_id || '',
      ...prepopulatedDemoValues,
    },
  })

  const onSubmit: SubmitHandler<EmployeeInput> = async (data, e) => {
    window.segment?.track('[ER] Add/edit employee button clicked', {
      is_create: isCreate,
      first_name: watch('first_name'),
      last_name: watch('last_name'),
      work_email: watch('work_email'),
      personal_email: watch('personal_email'),
      employment_subtype: watch('employment_subtype'),
      pay_type: watch('pay_type'),
      start_date: watch('start_date'),
      phone_number: watch('phone_number'),
    })

    // Use data-action to determine if the user wants to invite the employee after creating or updating.
    const isAddAndInviteFlow =
      e?.target.getAttribute('data-action') === 'inviteEmployee'

    // TODO: Implement API method for PUT request to edit an employee.
    try {
      setSubmitting(true)
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        isCreate ? 'post' : 'patch',
        isCreate ? '/api/employees' : `/api/employees/${employee?.id}`,
        {
          body: {
            first_name: data.first_name,
            last_name: data.last_name,
            personal_email: data.personal_email,
            work_email: data.work_email,
            employment_subtype: data.employment_subtype,
            start_date: data.start_date,
            pay_type: data.pay_type,
            phone_number: data.phone_number,
            native_employee_id: data.native_employee_id,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const response = await request.perform()
      if (response.ok) {
        const { employee }: { employee: Employee } = await response.json

        // Refetch employees to update the table and close the Sheet component.
        fetchEmployees()
        closeCreateOrEditEmployee()

        // Reset form.
        reset()

        // Display success toast.
        const toastMsg = `Employee successfully ${
          isCreate ? 'created' : 'updated'
        }`
        toast.success(toastMsg)

        // If the user wants to invite the employee, open the invite modal.
        if (isAddAndInviteFlow) {
          handleAddAndInvite(employee)
        }
      } else if (response.statusCode === 401 || response.statusCode === 403) {
        const { error } = await response.json
        console.error(
          `Error ${isCreate ? 'creating' : 'updating'} the employee${
            !isCreate ? ` ${employee!.id}` : ''
          }: `,
          error,
          response.statusCode
        )

        setSubmissionError({
          message: `There was an error ${isCreate ? 'creating' : 'updating'} this employee. ${error}`,
        })
      } else {
        const { error } = await response.json
        throw Error(error)
      }
    } catch (error: any) {
      console.error(
        `Error ${isCreate ? 'creating' : 'updating'} the employee${
          !isCreate ? ` ${employee!.id}` : ''
        }: `,
        error
      )
      setSubmissionError({
        message: `There was an error ${
        isCreate ? 'creating' : 'updating'
      } this employee. Please contact support.`,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openCreateOrEditEmployee()
    } else {
      closeCreateOrEditEmployee()
      reset()
      setSubmissionError(undefined)
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent aria-describedby={undefined} responsiveLayout>
          <SheetHeader>
            <SheetTitle className='pb-4'>
              <span className='heading-lg'>
                {isCreate ? 'Add Employee' : 'Edit Employee'}
              </span>
            </SheetTitle>
          </SheetHeader>
          {submissionError && (
            <div
              className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs'
              role='alert'
            >
              {submissionError.message}
              {submissionError.errors && (
                <ul>
                  {submissionError.errors.map((error) => (
                    <li key={error}>- {error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='flex flex-col py-6 gap-y-4'>
              <FormTextField<EmployeeInput>
                disabled={submitting}
                autoFocus
                error={errors.first_name}
                fieldId='first_name'
                fieldLabel='First name'
                register={register}
                validationRule={{ required: 'First name is required' }}
              />
              <FormTextField<EmployeeInput>
                disabled={submitting}
                error={errors.last_name}
                fieldId='last_name'
                fieldLabel='Last name'
                register={register}
                validationRule={{ required: 'Last name is required' }}
              />
              <FormTextField<EmployeeInput>
                disabled={submitting}
                error={errors.work_email}
                fieldId='work_email'
                fieldLabel='Work email'
                register={register}
                validationRule={{
                  validate: (value: string, formValues: EmployeeInput) =>
                    !value && !formValues.personal_email
                      ? 'Either work or personal email is required'
                      : undefined,
                }}
              />
              <FormTextField<EmployeeInput>
                disabled={submitting}
                error={errors.personal_email}
                fieldId='personal_email'
                fieldLabel='Personal email'
                register={register}
                validationRule={{
                  validate: (value: string, formValues: EmployeeInput) =>
                    !value && !formValues.work_email
                      ? 'Either work or personal email is required'
                      : undefined,
                }}
              />
              <FormTelephoneInputDS<EmployeeInput>
                disabled={submitting}
                error={errors.phone_number}
                fieldId='phone_number'
                fieldLabel='Cell phone'
                placeholder='(xxx) xxx-xxxx'
                register={register}
              />
              <FormTextField<EmployeeInput>
                disabled={submitting}
                error={errors.native_employee_id}
                fieldId='native_employee_id'
                fieldLabel={
                  <span className='flex items-center gap-1'>
                    Employee ID
                    <ThatchTooltip
                      text="This is the unique identifier for this employee in your company's system which will be appended to payroll reporting."
                      className='max-w-72 text-left'
                      includeArrow
                    >
                      <InfoIcon className='w-4 h-4 text-gray-400 hover:text-gray-500 cursor-help' />
                    </ThatchTooltip>
                  </span>
                }
                register={register}
              />
              <FormSelectField<EmployeeInput>
                disabled={submitting}
                error={errors.employment_subtype}
                fieldOptions={
                  isCreate
                    ? [
                        {
                          label: 'Full Time',
                          value: 'full_time',
                        },
                        {
                          label: 'Part Time',
                          value: 'part_time',
                        },
                      ]
                    : [
                        {
                          label: 'Full Time',
                          value: 'full_time',
                        },
                        {
                          label: 'Part Time',
                          value: 'part_time',
                        },
                        {
                          label: 'N/A',
                          value: '',
                        },
                      ]
                }
                fieldId='employment_subtype'
                fieldLabel='Employment'
                register={register}
                validationRule={{ required: 'Employment type is required' }}
              />
              <FormSelectField<EmployeeInput>
                disabled={submitting}
                error={errors.pay_type}
                fieldOptions={isCreate
                  ? [
                      {
                        label: 'Salary',
                        value: 'salary',
                      },
                      {
                        label: 'Hourly',
                        value: 'hourly',
                      },
                    ]
                  : [
                      {
                        label: 'Salary',
                        value: 'salary',
                      },
                      {
                        label: 'Hourly',
                        value: 'hourly',
                      },
                      {
                        label: 'N/A',
                        value: '',
                      },
                    ]}
                fieldId='pay_type'
                fieldLabel='Pay type'
                register={register}
                validationRule={{
                  validate: (value: string) => validatePayType(isCreate, value),
                }}
              />
              <FormDateInputField<EmployeeInput>
                disabled={submitting}
                serverError={errors.start_date}
                watch={watch}
                minDate={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 100)
                  )
                }
                maxDate={
                  new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                }
                fieldId='start_date'
                fieldLabel='Employment start date'
                onSelectDate={(date: Date | undefined) => {
                  // Convert date to string so the timezone doesn't affect how its passed to the API.
                  if (date) {
                    setValue('start_date', getDateString(date), {
                      shouldValidate: true,
                    })
                  }
                }}
              />
            </div>
            <footer>
              <button
                type='submit'
                className='btn-primary w-auto'
                disabled={submitting}
              >
                {isCreate
                  ? (
                    <UserPlusIcon size={16} className='mr-2' />
                    )
                  : (
                    <SaveIcon size={16} className='mr-2' />
                    )}
                {isCreate ? 'Add employee' : 'Save changes'}
              </button>
            </footer>
          </form>
          {/* Hiding add and invite functionality until we can improve this feature */}
          {/* {isCreate && (
            <div className='mt-4'>
              <button
                type='submit'
                className='btn-primary w-auto'
                disabled={submitting}
                data-action='inviteEmployee'
                onClick={handleSubmit(onSubmit)}
              >
                <SendIcon size={21} className='mr-2' />
                Add and invite
              </button>
            </div>
          )} */}
        </SheetContent>
      </Sheet>
    </div>
  )
}
